import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Facebook,
  Instagram,
  Linkedin,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";
const ICEF_LOGO = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/uybd5yuu_001bG000002DXv5QAG_badge.png";

const WHATSAPP_STUDENT = '923268096380';
const WHATSAPP_PARTNERSHIP = '923268096371';
const FACEBOOK_URL = 'https://www.facebook.com/share/1QFSsrjUfE/?mibextid=wwXIfr';

const Footer = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_STUDENT}?text=${encodeURIComponent('Hello! I would like to inquire about studying abroad.')}`;

  return (
    <footer className="bg-[#f1f3f5] dark:bg-slate-900 transition-colors duration-300">
      {/* Stats Banner */}
      <div className="bg-[#e9ecef] dark:bg-slate-800 border-y border-gray-200 dark:border-slate-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center justify-center gap-10">
            <div className="text-center group cursor-default">
              <p className="text-2xl font-semibold text-teal-700 dark:text-teal-400 group-hover:scale-105 transition-transform duration-300">10,000+</p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Happy Students</p>
            </div>
            <div className="h-10 w-px bg-gray-300 dark:bg-slate-600" />
            <div className="text-center group cursor-default">
              <p className="text-2xl font-semibold text-teal-700 dark:text-teal-400 group-hover:scale-105 transition-transform duration-300">100+</p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Partner Universities</p>
            </div>
            <div className="h-10 w-px bg-gray-300 dark:bg-slate-600" />
            <div className="text-center group cursor-default">
              <p className="text-2xl font-semibold text-teal-700 dark:text-teal-400 group-hover:scale-105 transition-transform duration-300">15+</p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Countries</p>
            </div>
            <div className="h-10 w-px bg-gray-300 dark:bg-slate-600" />
            <a href="https://www.icef.com/agency/001bG000002DXv5QAG" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
              <img src={ICEF_LOGO} alt="ICEF Certified" className="h-12 w-auto" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Brand & Dual CTA */}
          <div className="md:col-span-2 lg:col-span-4 space-y-6">
            <img src={LOGO_URL} alt="H E Direct" className="h-14 w-auto" />
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm">
              Your gateway to world-class education. We connect ambitious students with prestigious universities across the globe.
            </p>
            <div className="flex flex-col gap-3">
              <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-sm rounded-full transition-all duration-300 hover:-translate-y-0.5 w-fit">
                <Link to="/register" data-testid="footer-apply-btn">Start Your Journey <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-all duration-300 hover:-translate-y-0.5 w-fit">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-btn">Chat on WhatsApp</a>
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-gray-700 dark:text-gray-200 font-semibold text-sm mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-teal-600 dark:bg-teal-400 rounded-full"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li><Link to="/courses" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 hover:translate-x-1 inline-block text-sm">Search Courses</Link></li>
              <li><Link to="/destinations" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 hover:translate-x-1 inline-block text-sm">Destinations</Link></li>
              <li><Link to="/check-eligibility" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 hover:translate-x-1 inline-block text-sm">Check Eligibility</Link></li>
              <li><Link to="/agent-register" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 hover:translate-x-1 inline-block text-sm">Become an Agent</Link></li>
              <li><Link to="/help" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 hover:translate-x-1 inline-block text-sm">Help & Support</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-3">
            <h4 className="text-gray-700 dark:text-gray-200 font-semibold text-sm mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-teal-600 dark:bg-teal-400 rounded-full"></span>
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="tel:0913094815" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 flex items-center gap-2 group text-sm">
                  <Phone className="w-4 h-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                  091-3094815 (Landline)
                </a>
              </li>
              <li>
                <a href="tel:03329363599" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 flex items-center gap-2 group text-sm">
                  <Phone className="w-4 h-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                  0332-9363599 (Students)
                </a>
              </li>
              <li>
                <a href="tel:03268096380" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 flex items-center gap-2 group text-sm">
                  <Phone className="w-4 h-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                  0326-8096380 (Partnerships)
                </a>
              </li>
              <li>
                <a href="mailto:info@hedirect.org" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 flex items-center gap-2 group text-sm">
                  <Mail className="w-4 h-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                  info@hedirect.org
                </a>
              </li>
              <li>
                <a href="mailto:partnerships@hedirect.org" className="text-gray-500 dark:text-gray-400 hover:text-teal-700 dark:hover:text-teal-400 transition-all duration-200 flex items-center gap-2 group text-sm">
                  <Mail className="w-4 h-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform flex-shrink-0" />
                  partnerships@hedirect.org
                </a>
              </li>
            </ul>
          </div>

          {/* Office Locations */}
          <div className="lg:col-span-3">
            <h4 className="text-gray-700 dark:text-gray-200 font-semibold text-sm mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-teal-600 dark:bg-teal-400 rounded-full"></span>
              Our Offices
            </h4>
            <div className="space-y-4">
              {/* Main Office - Peshawar */}
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 dark:text-gray-200 text-sm font-medium">Head Office - Peshawar</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed">
                    Rafeys Height, 2nd Floor, Flat 306,<br />
                    Abdara Road, Near MM Academy,<br />
                    Main University Road, Peshawar
                  </p>
                </div>
              </div>
              {/* Regional Office - Islamabad */}
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 dark:text-gray-200 text-sm font-medium">Regional Office - Islamabad</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed">
                    Office No 1, 3rd Floor,<br />
                    Silver City Plaza, G11 Markaz,<br />
                    Sachal Sarmast Road, Islamabad
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 dark:border-slate-700 bg-[#e9ecef] dark:bg-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">© {new Date().getFullYear()} H E Direct Consultants. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-blue-500 hover:border-blue-500 hover:text-white transition-all duration-300 hover:-translate-y-1" data-testid="footer-social-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:border-transparent hover:text-white transition-all duration-300 hover:-translate-y-1">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-blue-600 hover:border-blue-600 hover:text-white transition-all duration-300 hover:-translate-y-1">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
